import React, {
  ChangeEvent,
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Mp3Encoder } from '@breezystack/lamejs'
import { IconPlayerStopFilled } from '@tabler/icons-react'
import { chunk } from 'lodash'
import Image from 'next/image'
import { useRouter } from 'next/router'
import type { Options, RecordRTCPromisesHandler } from 'recordrtc'
import styled from 'styled-components'
import type { Editor } from 'tinymce'

import AiMikeIcon from '../../../public/ai-mike-icon.svg'
import { Form } from '../shared-components/BUI/Form'
import { InputProps } from '../shared-components/BUI/Input'
import { Text } from '../shared-components/BUI/Text'

import { useUser } from '@/context/userContext'
import { useRegisterAIToolUsage } from '@/hooks/useRegisterAIToolUsage'

const BACKOFF_CHUNKS = [
  { max: 5, skip: 1 }, // from chunk 1-5, send every chunk
  { max: 11, skip: 2 }, // from chunk 6-11, send every 2nd chunk
  { max: 20, skip: 3 }, // from chunk 12-20, send every 3rd chunk
  { max: 99999, skip: 5 }, // from chunk 21+, send every 5th chunk
]

const Wrapper = styled.button`
  border-radius: 999px;
  background: white;
  padding: 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  border: 1px solid ${({ theme }) => theme.colour.ai.light};
  align-self: flex-start;
  cursor: pointer;
`

const Recorder = styled.div<{ recording: boolean }>`
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  background: ${({ recording, theme }) =>
    recording ? theme.colour.ai.dark : theme.colour.ai.light};
  color: ${({ recording, theme }) =>
    recording ? theme.colour.neutral.white : theme.colour.secondary.base};
  border: none;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`

const Visualiser = styled.div<{ show: boolean }>`
  width: ${({ show }) => (show ? '90px' : '0')};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  overflow: hidden;

  transition: width 0.2s;
`

const Cell = styled.div`
  width: 0.5rem;
  background: ${({ theme }) => theme.colour.ai.light};
  border-radius: 999px;
`

const Countdown = styled.div`
  ${({ theme }) => theme.font.caption01}
  color: ${({ theme }) => theme.colour.secondary._400};
  padding-left: 0.25rem;
`

const BARS = 4
const OLD_RANGE = { min: 0, max: 255 }
const NEW_RANGE = { min: 0, max: 20 }

const getCellId = (i: number) => `bp-visualiser-${i}`

const convertRange = (value: number, oldRange: Range, newRange: Range) => {
  return (
    ((value - oldRange.min) * (newRange.max - newRange.min)) /
      (oldRange.max - oldRange.min) +
    newRange.min
  )
}

const setCellHeight = (i: number, chunks: number[][]) => {
  const chunk = chunks[i]
  const sum = chunk.reduce((acc, val) => acc + val, 0)
  const avg = sum / chunk.length

  const value = convertRange(avg, OLD_RANGE, NEW_RANGE)

  document
    .getElementById(getCellId(i))
    ?.setAttribute('style', `height: ${value}px`)
}

const formatOutput = (currentContent: string, text: string) => {
  const conjunction = currentContent?.length ? '\n' : ''
  return currentContent + conjunction + text
}

const getUseageAction = (pathname: string, usageLocation?: string) => {
  const formItem = usageLocation ? ` - ${usageLocation}` : ''

  const chunks = pathname.split('/').filter(Boolean)
  const noDynamicChunks = chunks.filter((chunk) => !chunk.startsWith('['))

  return 'audio transcribe - ' + noDynamicChunks.join('/') + formItem
}

type Range = {
  min: number
  max: number
}

enum State {
  Idle,
  Recording,
}

type Props = {
  usageLocation?: string
  onInterimText?: (transcript: string) => void
  onFinalText?: (transcript: string) => void
  title?: string
  helpText?: string
  disabled?: boolean
  onFinishInput?: () => void
  maxTimeSeconds?: number
}

export const AudioTranscriber: React.FC<Props> = ({
  usageLocation,
  onInterimText,
  onFinalText,
  title,
  helpText,
  disabled = false,
  onFinishInput,
  maxTimeSeconds = 30,
}) => {
  const router = useRouter()

  const stream = useRef<MediaStream>(undefined)
  const source = useRef<MediaStreamAudioSourceNode>(undefined)
  const analyser = useRef<AnalyserNode>(undefined)
  const audioContext = useRef<AudioContext>(undefined)

  const chunks = useRef<Blob[]>([])
  const encoder = useRef<Mp3Encoder>(undefined)
  const recorder = useRef<RecordRTCPromisesHandler>(undefined)

  const voiceDetected = useRef(false)
  const chunkSkipCounter = useRef(0)
  const startRecordTime = useRef<number>(undefined)
  const autoCancelTimeout = useRef<NodeJS.Timeout>(undefined)

  const { registerUsage } = useRegisterAIToolUsage('audioTranscriber')

  const [state, setState] = useState(State.Idle)

  // if we are updating interim text, we need to limit the recording time, if not it's just one big chunk at the end so who cares
  const maxRecordTime = useMemo(() => {
    if (onInterimText) {
      return Math.min(maxTimeSeconds, 60)
    }
    return maxTimeSeconds
  }, [maxTimeSeconds, onInterimText])

  const clearAutoStop = useCallback(() => {
    if (autoCancelTimeout.current) {
      clearTimeout(autoCancelTimeout.current)
      autoCancelTimeout.current = undefined
    }
  }, [])

  const clearEverything = useCallback(() => {
    clearAutoStop()
    startRecordTime.current = undefined
    voiceDetected.current = false

    if (chunks.current) {
      chunks.current = []
    }

    if (encoder.current) {
      encoder.current.flush()
      encoder.current = undefined
    }

    if (recorder.current) {
      recorder.current.destroy()
      recorder.current = undefined
    }

    if (stream.current) {
      stream.current.getTracks().forEach((track) => track.stop())
      stream.current = undefined
    }
  }, [clearAutoStop])

  // on unmount, clear all resources˝
  useEffect(() => {
    return () => {
      clearEverything()
    }
  }, [clearEverything])

  // audio visualiser useEffect loop
  useEffect(() => {
    if (!analyser.current) return

    let looping = false

    const bufferLength = analyser.current.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    const getBarHeights = () => {
      if (!looping) return

      analyser.current.getByteFrequencyData(dataArray) // mutates dataArray
      const chunks = chunk(dataArray, 2)
      Array.from({ length: BARS }).forEach((_, i) => setCellHeight(i, chunks))

      if (startRecordTime.current) {
        const secondsRemaining = Math.max(
          0,
          maxRecordTime - (Date.now() - startRecordTime.current) / 1000
        )
        document.getElementById('bp-voice-countdown').innerText =
          secondsRemaining.toFixed(0) + 's'
      }

      // there are some glitchy behaviours with sending silence audio clips soooo let's use the visualiser to detect voice
      if (!voiceDetected.current) {
        const detected = chunks.some((chunk, index) => {
          if (index <= 2) return false // ignore the super low end which can be background noise

          const humanVoiceLevel = chunk.some((c) => c >= 85) // 85hz is a good threshold for human voice
          return humanVoiceLevel
        })

        voiceDetected.current = detected
      }

      requestAnimationFrame(getBarHeights)
    }

    if (state === State.Recording) {
      looping = true
      requestAnimationFrame(getBarHeights)
    } else {
      document.getElementById('bp-voice-countdown').innerText = ''
    }

    return () => {
      looping = false
    }
  }, [state, maxRecordTime])

  const onStartRecording = async () => {
    try {
      clearAutoStop()

      audioContext.current = new AudioContext()
      analyser.current = audioContext.current.createAnalyser()
      analyser.current.fftSize = 32

      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      })

      source.current = audioContext.current.createMediaStreamSource(
        stream.current
      )

      source.current.connect(analyser.current)

      const { default: RecordRTC } = await import('recordrtc')
      const recorderConfig: Options = {
        mimeType: 'audio/wav',
        numberOfAudioChannels: 1, // mono
        recorderType: RecordRTC.StereoAudioRecorder,
        sampleRate: 44100, // Sample rate = 44.1khz
        timeSlice: 1_000,
        type: 'audio',
        ondataavailable: onInterimText ? onDataAvailable : undefined,
      }
      recorder.current = new RecordRTC.RecordRTCPromisesHandler(
        stream.current,
        recorderConfig
      )

      encoder.current = new Mp3Encoder(1, 44100, 96)

      const recordState = await recorder.current.getState()

      if (recordState === 'inactive' || recordState === 'stopped') {
        await recorder.current.startRecording()
      }

      if (recordState === 'paused') {
        await recorder.current.resumeRecording()
      }

      startRecordTime.current = Date.now()
      autoCancelTimeout.current = setTimeout(() => {
        onStopRecording()
        setState(State.Idle)
      }, maxRecordTime * 1000)

      registerUsage(getUseageAction(router.pathname, usageLocation))
    } catch (err) {
      console.error(err)
    }
  }

  const onStopRecording = async () => {
    try {
      onFinishInput?.()
      clearAutoStop()

      if (recorder.current) {
        const recordState = await recorder.current.getState()

        if (recordState === 'recording' || recordState === 'paused') {
          await recorder.current.stopRecording()
        }

        let blob = await recorder.current.getBlob()
        const buffer = await blob.arrayBuffer()

        const mp3 = encoder.current.encodeBuffer(new Int16Array(buffer))
        blob = new Blob([mp3], { type: 'audio/mpeg' })
        const file = new File([blob], 'speech.mp3', { type: 'audio/mpeg' })

        await transcribeAudio(file, true)

        clearEverything()
      }
    } catch (err) {
      console.error(err)
    }
  }

  const onRecorderClick = async () => {
    if (disabled) return

    const newState = state === State.Idle ? State.Recording : State.Idle

    if (newState === State.Recording) {
      onStartRecording()
    } else {
      onStopRecording()
    }

    setState(newState)
  }

  const onDataAvailable = async (data: Blob) => {
    try {
      if (recorder.current) {
        // add mp3 encoding chunk to chunks array
        if (encoder.current) {
          const buffer = await data.arrayBuffer()
          const mp3chunk = encoder.current.encodeBuffer(new Int16Array(buffer))
          const mp3blob = new Blob([mp3chunk], { type: 'audio/mpeg' })
          chunks.current.push(mp3blob)
        }

        const recorderState = await recorder.current.getState()

        // find the backoff chunk size
        const backoff = BACKOFF_CHUNKS.find(
          (c) => (chunks.current.length ?? 0) <= c.max
        )

        // increment the skip counter
        chunkSkipCounter.current = chunkSkipCounter.current + 1

        // if we haven't reached the backoff limit, skip this chunk
        if (chunkSkipCounter.current < backoff.skip) {
          return
        } else {
          // reset the skip counter
          chunkSkipCounter.current = 0
        }

        // wait for the first voice detection to start sending audio to prevent strange transcriptions with silent audio
        if (!voiceDetected.current) {
          return
        }

        // if recorder is recording, send the audio chunk to the server
        if (recorderState === 'recording') {
          const blob = new Blob(chunks.current, {
            type: 'audio/mpeg',
          })
          const file = new File([blob], 'speech.mp3', {
            type: 'audio/mpeg',
          })
          transcribeAudio(file, false)
        }
      }
    } catch (err) {
      console.error(err)
    }
  }

  const transcribeAudio = async (file: File, final: boolean) => {
    const body = new FormData()
    body.append('file', file)

    const response = await fetch('/api/ai/transcribeAudio', {
      method: 'POST',
      body,
    })

    const { text } = await response.json()

    if (final) {
      onFinalText?.(text)
    } else {
      onInterimText?.(text)
    }
  }

  return (
    <Wrapper
      type="button"
      onClick={(event) => {
        if (!disabled) {
          // This is useful to allow Popconfirms to fire on the button, e.g. to tell the user to turn this on in settings
          event.stopPropagation()
          onRecorderClick()
        }
      }}
    >
      <Recorder recording={state === State.Recording}>
        {state === State.Idle ? (
          <Image src={AiMikeIcon} alt="AI microphone" width={22} height={22} />
        ) : (
          <IconPlayerStopFilled size={16} />
        )}
      </Recorder>
      {title && state === State.Idle && (
        <Text
          variant="label02"
          strong
          style={{ marginLeft: '0.25rem', marginRight: '0.25rem' }}
        >
          {title}
        </Text>
      )}
      <Visualiser show={state === State.Recording}>
        {Array.from({ length: BARS }).map((_, i) => (
          <Cell key={`bar-${i}`} id={getCellId(i)} />
        ))}
        <Countdown id="bp-voice-countdown" />
      </Visualiser>
      {state === State.Recording && helpText && (
        <Text
          variant="label02"
          strong
          style={{ marginLeft: '0.25rem', marginRight: '0.25rem' }}
        >
          {helpText}
        </Text>
      )}
    </Wrapper>
  )
}

const BaseWrapper = styled.div`
  position: relative;

  & ${Wrapper} {
    position: absolute;
    right: 0.375rem;
    bottom: 0.375rem;
    z-index: 1;
  }
`

const InputWrapper = styled(BaseWrapper)``

const TinyMceWrapper = styled(BaseWrapper)`
  & ${Wrapper} {
    bottom: 2.5rem;
    right: 1rem;
  }
`

type AutoProps = Props & {
  mode?: 'Input' | 'TinyMCE'
  formItemName: string
  editorRef?: React.MutableRefObject<Editor>
  usageLocation?: string
  children?: React.ReactNode
  value?: string
  onChange?: (value: string | ChangeEvent<HTMLInputElement>) => void
}

export const AutoAudioTranscriber: React.FC<AutoProps> = ({
  mode = 'Input',
  formItemName,
  editorRef,
  onFinalText,
  onInterimText,
  value,
  onChange,
  usageLocation,
  children,
  ...otherFormProps
}) => {
  const form = Form.useFormInstance()
  const memoedValue = useRef<string>(undefined)

  const user = useUser()

  const childrenWithProps = Children.map(children, (child) => {
    if (isValidElement<InputProps>(child)) {
      return cloneElement(child, { value, onChange, ...otherFormProps })
    }
    return child
  })

  if (!user?.aiTools?.aiToolsEnabled) {
    return <>{childrenWithProps}</>
  }

  const onInternalTextUpdate = (text: string, final: boolean) => {
    if (memoedValue.current === undefined) {
      memoedValue.current = form.getFieldValue(formItemName) ?? ''
    }

    const newValue = formatOutput(memoedValue.current, text)

    if (editorRef?.current) {
      editorRef.current.setContent(newValue)
    }

    onChange?.(newValue)

    if (final) {
      onFinalText?.(text)
      memoedValue.current = undefined
    } else {
      onInterimText?.(text)
    }
  }

  const onInternalInterimText = (text: string) => {
    onInternalTextUpdate(text, false)
  }

  const onInternalFinalText = (text: string) => {
    onInternalTextUpdate(text, true)
  }

  if (mode === 'Input') {
    return (
      <InputWrapper>
        {childrenWithProps}
        <AudioTranscriber
          usageLocation={usageLocation ?? formItemName}
          onInterimText={onInternalInterimText}
          onFinalText={onInternalFinalText}
        />
      </InputWrapper>
    )
  }

  if (mode === 'TinyMCE') {
    return (
      <TinyMceWrapper>
        {children}
        <AudioTranscriber
          usageLocation={usageLocation ?? formItemName}
          onInterimText={onInternalInterimText}
          onFinalText={onInternalFinalText}
        />
      </TinyMceWrapper>
    )
  }
}
